import axios from "axios";
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearAuthData,
} from "../utils/auth";
import { ErrorResponse } from "../types/responses/api-response";
import { redirectToLoginPreservingLocation } from "../utils/return-url";

// Определяем пользовательский тип для ошибки с нашей структурой данных
export interface ApiErrorResponse extends ErrorResponse {
  status: number;
  statusText: string;
}

// Создаем инстанс axios с базовым URL и заголовками
const api = axios.create({
  baseURL: process.env["REACT_APP_API_BASE_URL"] || "http://localhost:3001/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Интерцептор для обработки токена авторизации (async для Secure Storage)
api.interceptors.request.use(
  async (config) => {
    const token = await getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Интерцептор для обработки ошибок и обновления токена
api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    // Если ошибка 401 (Unauthorized) и запрос не для обновления токена
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Получаем новый токен с помощью refresh токена
        const refreshToken = await getRefreshToken();
        if (!refreshToken) {
          throw new Error("Refresh token not found");
        }

        const { accessToken, refreshToken: newRefreshToken } = await axios
          .post(
            `${process.env["REACT_APP_API_BASE_URL"] || "http://localhost:3001/api"}/auth/refresh`,
            { refreshToken },
          )
          .then((res) => res.data);

        // Сохраняем новые токены
        await setTokens(accessToken, newRefreshToken);

        // Обновляем заголовок и повторяем запрос
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Если не удалось обновить токен, выходим из системы. С публичных страниц
        // хелпер НЕ уводит (бутстрап-401 гостя — штатно), с защищённых — сохраняет
        // место в sessionStorage.returnUrl (SSOT return-url.ts) и ведёт на /auth/login.
        await clearAuthData();
        redirectToLoginPreservingLocation();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
